import { useCallback, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Card, NavLink, Stack, Text } from '@mantine/core';
import { PageHeader } from '../components/ui/PageHeader';
import { AnnualWorkingHoursPanel } from './settings/AnnualWorkingHoursPanel';
import { FuelAshCategoriesPanel } from './settings/FuelAshCategoriesPanel';
import classes from './SettingsPage.module.css';

/**
 * Meldungsart, zu der eine Einstellung gehört (D-18, D-20).
 *
 * Die Gliederung nach Meldungsart ist seit diesem Umbau (33-06, Sketch 018 Variante B) das
 * NAVIGATIONSZIEL der Einstellungsseite: links die Meldungsarten, rechts der Inhalt der
 * ausgewählten. Eine Meldungsart ohne eigene Einstellungen erscheint in der Navigation gar
 * nicht erst — `SETTING_SECTIONS` unten führt nur die Kategorien mit tatsächlichem Inhalt, nicht
 * alle drei. Eine spätere Rechtegrenze (wer nur Umweltdaten verwaltet, sieht nur diesen Eintrag)
 * wäre damit ein reiner Filter auf diese Liste — das war der Ausschlag für Variante B gegenüber
 * einer Seite mit Rubriken (Sketch-README, Entscheidung 2026-09-07).
 */
type SettingCategory = 'accident' | 'environmental' | 'market';

const CATEGORY_LABELS: Record<SettingCategory, string> = {
  accident: 'Unfalldaten',
  environmental: 'Umweltdaten',
  market: 'Marktdaten',
};

/** Kategorien mit tatsächlichem Inhalt, Reihenfolge accident/environmental/market (D-18) —
 *  `environmental` trägt seit 33-07 die Aschegehalt-Stammdaten. */
const SETTING_SECTIONS: SettingCategory[] = ['accident', 'environmental'];

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Navigationszähler je Kategorie (Sketch 018: "Unfalldaten 1" / "Umweltdaten 23"). Bei der
  // KANÜ-Einstellung ist die Zahl immer 1 (ein Datensatz); der Setter bleibt derselbe für den
  // künftigen Umweltdaten-Bereich (33-07), dessen Zeilenzahl sich tatsächlich ändert.
  const [sectionCounts, setSectionCounts] = useState<Partial<Record<SettingCategory, number>>>({
    accident: 1,
  });

  const requestedSection = searchParams.get('section');
  const activeSection: SettingCategory = SETTING_SECTIONS.includes(requestedSection as SettingCategory)
    ? (requestedSection as SettingCategory)
    : SETTING_SECTIONS[0];

  function handleSelectSection(category: SettingCategory) {
    setSearchParams({ section: category }, { replace: true });
  }

  // Stabile Identitaet: FuelAshCategoriesPanel laedt beim Mounten und ruft den Zaehler beim
  // Laden/Speichern/Loeschen erneut auf -- ohne useCallback wuerde jeder SettingsPage-Render eine
  // neue Funktionsreferenz an die Panel-Props geben.
  const handleEnvironmentalCountChange = useCallback((count: number) => {
    setSectionCounts((prev) => ({ ...prev, environmental: count }));
  }, []);

  function renderSectionContent(category: SettingCategory): ReactNode {
    switch (category) {
      case 'accident':
        return (
          <AnnualWorkingHoursPanel
            onCountChange={(count) => setSectionCounts((prev) => ({ ...prev, accident: count }))}
          />
        );
      case 'environmental':
        return <FuelAshCategoriesPanel onCountChange={handleEnvironmentalCountChange} />;
      default:
        return null;
    }
  }

  return (
    <Stack>
      <PageHeader
        title="Einstellungen"
        breadcrumb={{ label: 'Administration' }}
        description="Zentral gepflegte Werte, die in die Berechnung der Meldungen einfließen."
      />

      <div className={classes.split}>
        <nav className={classes.nav} aria-label="Einstellungsbereiche" data-testid="settings-nav">
          {SETTING_SECTIONS.map((category) => {
            const isActive = category === activeSection;
            const count = sectionCounts[category];
            return (
              <NavLink
                key={category}
                component="button"
                type="button"
                label={CATEGORY_LABELS[category]}
                active={isActive}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`settings-nav-${category}`}
                rightSection={
                  count !== undefined ? (
                    <Text size="xs" c="dimmed">
                      {count}
                    </Text>
                  ) : undefined
                }
                onClick={() => handleSelectSection(category)}
              />
            );
          })}
        </nav>

        {/* Alle Bereiche bleiben gemountet (nur `display: none` am inaktiven) -- der Wechsel darf
            ungespeicherte Eingaben nicht verwerfen, ohne eigenen Schutzdialog zu brauchen. Das
            `hidden`-Attribut kommt bewusst NICHT zum Einsatz: es überstimmt die Flex-Anzeige der
            Mantine-`Card`. */}
        <div>
          {SETTING_SECTIONS.map((category) => (
            <Card
              key={category}
              data-testid={`settings-section-${category}`}
              style={category === activeSection ? undefined : { display: 'none' }}
            >
              {renderSectionContent(category)}
            </Card>
          ))}
        </div>
      </div>
    </Stack>
  );
}
